import { QueryClient } from '@tanstack/react-query';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { get, set, del } from 'idb-keyval';
import { cacheDebugLog, installCacheDebugListeners } from '../offline/utils/cache-debug.js';
import { offlineStateService } from '../offline/services/offline-state.service.js';

// Custom IndexedDB storage for larger data
const idbStorage = {
  getItem: async (key) => {
    try {
      const value = await get(key);
      cacheDebugLog('rq-persist-idb-get', { key, hit: !!value });
      return value || null;
    } catch (error) {
      console.error('Error reading from IndexedDB:', error);
      return null;
    }
  },
  setItem: async (key, value) => {
    try {
      const size = typeof value === 'string' ? value.length : 0;
      cacheDebugLog('rq-persist-idb-set', { key, approxChars: size });
      await set(key, value);
    } catch (error) {
      console.error('Error writing to IndexedDB:', error);
    }
  },
  removeItem: async (key) => {
    try {
      await del(key);
    } catch (error) {
      console.error('Error removing from IndexedDB:', error);
    }
  },
};

// Create async storage persister using IndexedDB
export const persister = createAsyncStoragePersister({
  storage: idbStorage,
  key: 'gym-app-react-query-cache',
  throttleTime: 1000, // Save at most once per second
});

// Query Client Configuration
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is considered fresh for 0 minutes (always fetch fresh data)
      staleTime: 0, // 0 minutes
      // Cache data for 7 days even if unused (matches offline grace period)
      gcTime: 7 * 24 * 60 * 60 * 1000, // 7 days
      // Don't refetch on window focus or reconnect for offline-first behavior
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      retry: (failureCount, error) => {
        offlineStateService.syncFromNavigator();
        if (offlineStateService.isCurrentlyOffline()) return false;
        if (offlineStateService.isCurrentlyDegraded()) return false;
        if (String(error?.message || '').includes('Offline')) return false;
        return failureCount < 3;
      },
      // Retry delay increases with each attempt
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      retry: (failureCount, error) => {
        offlineStateService.syncFromNavigator();
        if (offlineStateService.isCurrentlyOffline()) return false;
        if (offlineStateService.isCurrentlyDegraded()) return false;
        if (error?.queued) return false;
        return failureCount < 1;
      },
    },
  },
});

installCacheDebugListeners(queryClient);

// Query Keys - centralized for consistency
export const QueryKeys = {
  // Auth/User
  USER: ['user'],
  PROFILE: (userId) => ['profile', userId],

  // Dashboard
  DASHBOARD: ['dashboard'],
  GYM_DASHBOARD: ['gym-dashboard'],
  ATHLETE_DASHBOARD: ['athlete-dashboard'],
  TRAINER_DASHBOARD: ['trainer-dashboard'],

  // Athletes
  ATHLETES: ['athletes'],
  ATHLETE: (id) => ['athletes', id],
  ATHLETE_LIST: (params) => ['athletes', 'list', params],

  // Trainers
  TRAINERS: ['trainers'],
  TRAINER: (id) => ['trainers', id],

  // Programs
  PROGRAMS: ['programs'],
  PROGRAM: (id) => ['programs', id],

  // Diets
  DIETS: ['diets'],
  DIET: (id) => ['diets', id],

  // Daybook
  DAYBOOKS: ['daybooks'],
  DAYBOOK: (id) => ['daybooks', id],
  DAYBOOKS_BY_PERSON: (personId) => ['daybooks', 'person', personId],

  // Payments
  PAYMENTS: ['payments'],

  // Settings
  SETTINGS: ['settings'],

  // Membership
  MEMBERSHIP: ['membership'],

  // Gym
  GYM: ['gym'],
  GYM_ME: ['gym', 'me'],

  // Exercises & Meals
  EXERCISES: ['exercises'],
  MEALS: ['meals'],

  // Lockers
  LOCKERS: ['lockers'],
  LOCKER_CONFIG: ['lockers', 'config'],
};

// Helper function to prefetch common queries after login
export const prefetchCommonQueries = async (user) => {
  if (!user) return;

  const prefetchPromises = [];

  // Prefetch user profile
  if (user.role === 'athlete' && user.athleteId) {
    prefetchPromises.push(
      queryClient.prefetchQuery({
        queryKey: QueryKeys.PROFILE(user.athleteId),
        queryFn: () =>
          import('../services/athlete.service').then((m) =>
            m.athleteService.getAthleteById(user.athleteId)
          ),
        staleTime: 5 * 60 * 1000,
      })
    );
    prefetchPromises.push(
      queryClient.prefetchQuery({
        queryKey: QueryKeys.ATHLETE_DASHBOARD,
        queryFn: () =>
          import('../services/athlete.service').then((m) =>
            m.athleteService.getMyDashboard()
          ),
        staleTime: 5 * 60 * 1000,
      })
    );
  } else if (user.role === 'trainer' && user.trainerId) {
    prefetchPromises.push(
      queryClient.prefetchQuery({
        queryKey: QueryKeys.PROFILE(user.trainerId),
        queryFn: () =>
          import('../services/trainer.service').then((m) =>
            m.trainerService.getTrainerById(user.trainerId)
          ),
        staleTime: 5 * 60 * 1000,
      })
    );
    prefetchPromises.push(
      queryClient.prefetchQuery({
        queryKey: QueryKeys.TRAINER_DASHBOARD,
        queryFn: () =>
          import('../services/trainer.service').then(
            (m) =>
              m.trainerService.getTrainerDashboard?.() || Promise.resolve(null)
          ),
        staleTime: 5 * 60 * 1000,
      })
    );
  } else if (user.role === 'gym') {
    prefetchPromises.push(
      queryClient.prefetchQuery({
        queryKey: QueryKeys.GYM_ME,
        queryFn: () =>
          import('../services/gym.service').then((m) => m.gymService.getMe()),
        staleTime: 5 * 60 * 1000,
      })
    );
    prefetchPromises.push(
      queryClient.prefetchQuery({
        queryKey: QueryKeys.GYM_DASHBOARD,
        queryFn: () =>
          import('../services/gym.service').then((m) =>
            m.gymService.getGymDashboard()
          ),
        staleTime: 5 * 60 * 1000,
      })
    );
  }

  // Prefetch commonly used lists
  prefetchPromises.push(
    queryClient.prefetchQuery({
      queryKey: QueryKeys.ATHLETES,
      queryFn: () =>
        import('../services/athlete.service').then((m) =>
          m.athleteService.getAthletes({})
        ),
      staleTime: 5 * 60 * 1000,
    })
  );

  prefetchPromises.push(
    queryClient.prefetchQuery({
      queryKey: QueryKeys.TRAINERS,
      queryFn: () =>
        import('../services/trainer.service').then((m) =>
          m.trainerService.getTrainers()
        ),
      staleTime: 5 * 60 * 1000,
    })
  );

  prefetchPromises.push(
    queryClient.prefetchQuery({
      queryKey: QueryKeys.PROGRAMS,
      queryFn: () =>
        import('../services/program.service').then((m) =>
          m.programService.getPrograms()
        ),
      staleTime: 5 * 60 * 1000,
    })
  );

  prefetchPromises.push(
    queryClient.prefetchQuery({
      queryKey: QueryKeys.SETTINGS,
      queryFn: () =>
        import('../services/settings.service').then((m) =>
          m.settingsService.getSettings()
        ),
      staleTime: 5 * 60 * 1000,
    })
  );

  // Prefetch exercise library (for workout program creation)
  prefetchPromises.push(
    queryClient.prefetchQuery({
      queryKey: [...QueryKeys.EXERCISES, 1, 1000, {}],
      queryFn: () =>
        import('../services/program.service').then((m) =>
          m.programService.getExercises({ page: 1, limit: 1000 })
        ),
      staleTime: 5 * 60 * 1000,
    })
  );

  // Prefetch meal library (for diet program creation)
  prefetchPromises.push(
    queryClient.prefetchQuery({
      queryKey: [...QueryKeys.MEALS, 1, 1000, {}],
      queryFn: () =>
        import('../services/diet.service').then((m) =>
          m.dietService.getMeals({ page: 1, limit: 1000 })
        ),
      staleTime: 5 * 60 * 1000,
    })
  );

  // Execute all prefetch requests in parallel
  await Promise.allSettled(prefetchPromises);
};

// Helper to clear all queries on logout
export const clearQueryCache = () => {
  queryClient.clear();
};
